import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, where, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { getWashPackages } from './washPackages';
import { getFunctions, httpsCallable } from 'firebase/functions';
import QRCode from 'qrcode';
import AdminWashCodes from './AdminWashCodes';

function Admin() {
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [userBlocks, setUserBlocks] = useState({}); // Store user blocks
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    todayOrders: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    activeUsers: 0
  });
  
  // Filter and search states
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;
    return {
      location: '',
      date: isoDate,
      dateDisplay: `${day}/${month}/${year}`,
      washType: '',
      status: '',
      appointmentType: ''
    };
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  
  // Analytics states
  const [analyticsDateRange, setAnalyticsDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });
  const [selectedAnalyticsTopic, setSelectedAnalyticsTopic] = useState('profits');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [isViewOnlyAdmin, setIsViewOnlyAdmin] = useState(false);
  const [adminSession, setAdminSession] = useState(null);
  
  // QR Code modal states
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');

  
  const navigate = useNavigate();
  
  // Fetch user blocks
  const fetchUserBlocks = async () => {
    try {
      console.log('🔍 Fetching user blocks...');
      
      // Fetch special appointment blocks
      const specialBlocksQuery = query(collection(db, 'specialAppointmentBlocks'));
      const specialBlocksSnapshot = await getDocs(specialBlocksQuery);
      console.log('🔍 Special blocks found:', specialBlocksSnapshot.docs.length);
      
      // Fetch free appointment blocks
      const freeBlocksQuery = query(collection(db, 'freeAppointmentBlocks'));
      const freeBlocksSnapshot = await getDocs(freeBlocksQuery);
      console.log('🔍 Free blocks found:', freeBlocksSnapshot.docs.length);
      
      const blocks = {};
      
      // Process special blocks
      specialBlocksSnapshot.docs.forEach(doc => {
        const blockData = doc.data();
        console.log('🔍 Special block data:', blockData);
        blocks[blockData.userId] = {
          type: 'special',
          blockUntil: blockData.blockUntil?.toDate?.() || new Date(blockData.blockUntil),
          reason: blockData.reason,
          updatedAt: blockData.updatedAt?.toDate?.() || new Date(blockData.updatedAt)
        };
      });
      
      // Process free blocks
      freeBlocksSnapshot.docs.forEach(doc => {
        const blockData = doc.data();
        console.log('🔍 Free block data:', blockData);
        blocks[blockData.userId] = {
          type: 'free',
          blockUntil: blockData.blockUntil?.toDate?.() || new Date(blockData.blockUntil),
          reason: blockData.reason,
          updatedAt: blockData.updatedAt?.toDate?.() || new Date(blockData.updatedAt)
        };
      });
      
      console.log('✅ Fetched user blocks:', blocks);
      console.log('✅ Total blocked users:', Object.keys(blocks).length);
      setUserBlocks(blocks);
    } catch (error) {
      console.error('❌ Error fetching user blocks:', error);
    }
  };

  // Unblock user function
  const unblockUser = async (userId, blockType) => {
    try {
      console.log(`🔓 Unblocking user ${userId} from ${blockType} appointments`);
      const collectionName = blockType === 'special' ? 'specialAppointmentBlocks' : 'freeAppointmentBlocks';
      const blockDoc = doc(db, collectionName, userId);
      await deleteDoc(blockDoc);
      
      // Refresh user blocks to get latest data
      await fetchUserBlocks();
      
      console.log(`✅ Successfully unblocked user ${userId} from ${blockType} appointments`);
      alert(`User has been unblocked from ${blockType} appointments`);
    } catch (error) {
      console.error('❌ Error unblocking user:', error);
      alert('Failed to unblock user. Please try again.');
    }
  };


  useEffect(() => {
    console.log('Admin component mounted');
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // No user is signed in
        navigate('/adminpfy9665k01-auth');
        return;
      }
      
      try {
        // Get the user's ID token to check custom claims
        const idTokenResult = await user.getIdTokenResult();
        
        // Check if user has admin custom claims (full admin or view-only admin)
        if (!idTokenResult.claims || (!idTokenResult.claims.admin && !idTokenResult.claims.viewOnlyAdmin)) {
          console.log('User does not have admin privileges');
          navigate('/adminpfy9665k01-auth');
          return;
        }
        
        // Check if user is view-only admin
        if (idTokenResult.claims.viewOnlyAdmin && !idTokenResult.claims.admin) {
          console.log('User has view-only admin privileges:', idTokenResult.claims);
          setIsViewOnlyAdmin(true);
          setAdminSession({
            email: user.email || 'Unknown account',
            role: 'View-only admin'
          });
          // Set default tab to orders for view-only admins
          setActiveTab('orders');
        } else {
          console.log('User has full admin privileges:', idTokenResult.claims);
          setIsViewOnlyAdmin(false);
          setAdminSession({
            email: user.email || 'Unknown account',
            role: 'Full admin'
          });
          // Set default tab to dashboard for full admins
          setActiveTab('dashboard');
        }
        
        // User is authenticated and has admin privileges, fetch data
        fetchData();
        // Also fetch orders data for full admins
        if (!idTokenResult.claims.viewOnlyAdmin || idTokenResult.claims.admin) {
          fetchOrdersData();
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/adminpfy9665k01-auth');
        return;
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  const fetchData = async () => {
    console.log('fetchData function called');
    setLoading(true);
    try {
             // Fetch orders
       const ordersQuery = query(collection(db, 'orders'), orderBy('appointmentDate', 'desc'));
       const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Fetched orders:', ordersData);
      console.log('First order details:', ordersData[0]);
      setOrders(ordersData);

             // Fetch users
       const usersQuery = query(collection(db, 'users'));
       const usersSnapshot = await getDocs(usersQuery);
       const usersData = usersSnapshot.docs.map(doc => ({
         id: doc.id,
         ...doc.data()
       }));
       console.log('Fetched users:', usersData);
       console.log('Number of users:', usersData.length);
       setUsers(usersData);

      // Fetch user blocks
      await fetchUserBlocks();

             // Calculate stats
       const today = new Date();
       today.setHours(0, 0, 0, 0);
       
       // Today's completed orders (appointments with today's date and "completed" status)
       const todayCompletedOrders = ordersData.filter(order => {
         try {
           let orderDate;
           if (order.appointmentDate?.toDate) {
             orderDate = order.appointmentDate.toDate();
           } else if (order.appointmentDate instanceof Date) {
             orderDate = order.appointmentDate;
           } else if (order.appointmentDate) {
             orderDate = new Date(order.appointmentDate);
           } else {
             return false;
           }
           
           // Convert to UTC date string for comparison to avoid timezone issues
           const orderYear = orderDate.getUTCFullYear();
           const orderMonth = String(orderDate.getUTCMonth() + 1).padStart(2, '0');
           const orderDay = String(orderDate.getUTCDate()).padStart(2, '0');
           const orderDateStr = `${orderMonth}/${orderDay}/${orderYear}`;
           
           const todayYear = today.getUTCFullYear();
           const todayMonth = String(today.getUTCMonth() + 1).padStart(2, '0');
           const todayDay = String(today.getUTCDate()).padStart(2, '0');
           const todayStr = `${todayMonth}/${todayDay}/${todayYear}`;
           
           // Check if order is for today AND has completed status
           return orderDateStr === todayStr && order.status === 'completed';
         } catch (error) {
           console.error('Error calculating today completed orders:', error);
           return false;
         }
       });

       // All orders for today (regardless of status) - for "Today's Top Wash"
       const todayAllOrders = ordersData.filter(order => {
         try {
           let orderDate;
           if (order.appointmentDate?.toDate) {
             orderDate = order.appointmentDate.toDate();
           } else if (order.appointmentDate instanceof Date) {
             orderDate = order.appointmentDate;
           } else if (order.appointmentDate) {
             orderDate = new Date(order.appointmentDate);
           } else {
             return false;
           }
           
           // Convert to UTC date string for comparison to avoid timezone issues
           const orderYear = orderDate.getUTCFullYear();
           const orderMonth = String(orderDate.getUTCMonth() + 1).padStart(2, '0');
           const orderDay = String(orderDate.getUTCDate()).padStart(2, '0');
           const orderDateStr = `${orderMonth}/${orderDay}/${orderYear}`;
           
           const todayYear = today.getUTCFullYear();
           const todayMonth = String(today.getUTCMonth() + 1).padStart(2, '0');
           const todayDay = String(today.getUTCDate()).padStart(2, '0');
           const todayStr = `${todayMonth}/${todayDay}/${todayYear}`;
           
           return orderDateStr === todayStr;
         } catch (error) {
           console.error('Error calculating today all orders:', error);
           return false;
         }
       });

               const totalRevenue = ordersData.reduce((sum, order) => sum + (order.price || 0), 0);
        const todayRevenue = todayCompletedOrders.reduce((sum, order) => sum + (order.price || 0), 0);
        
        // Calculate today's top wash (most frequent service name from completed orders)
        const serviceCounts = {};
        todayCompletedOrders.forEach(order => {
          const serviceName = order.serviceName || order.serviceType || 'Unknown Service';
          serviceCounts[serviceName] = (serviceCounts[serviceName] || 0) + 1;
        });
        
        // Find the most frequent service
        let todayTopWash = 'No completed orders';
        let maxCount = 0;
        Object.entries(serviceCounts).forEach(([serviceName, count]) => {
          if (count > maxCount) {
            maxCount = count;
            todayTopWash = serviceName;
          }
        });
        
        // If there are multiple services with the same count, show the first one
        if (maxCount > 1) {
          todayTopWash = `${todayTopWash} (${maxCount}x)`;
        }

             setStats({
         totalOrders: ordersData.length,
         todayOrders: todayCompletedOrders.length,
         totalRevenue: totalRevenue,
         todayRevenue: todayRevenue,
         todayTopWash: todayTopWash,
         activeUsers: usersData.length
       });

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };



  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    let d;
    if (date.toDate) {
      // Firestore timestamp - convert to local date string directly
      const timestamp = date.toDate();
      // Use UTC methods to avoid timezone offset issues
      const year = timestamp.getUTCFullYear();
      const month = String(timestamp.getUTCMonth() + 1).padStart(2, '0');
      const day = String(timestamp.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    } else if (date instanceof Date) {
      // Already a Date object
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    } else {
      // String or timestamp
      d = new Date(date);
      const year = d.getUTCFullYear();
      const month = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#f59e0b';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      case 'trading': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  // Use Firestore document ID consistently
  const getDisplayOrderId = (order) => {
    return order.id;
  };

  // QR Code functions
  const generateQRCode = async (order) => {
    try {
        const orderData = {
          orderId: order.id,
          userId: order.userId,
          appointmentDate: order.appointmentDate,
          appointmentTime: formatTime12Hour(order.appointmentTime),
          location: order.location,
          status: order.status,
          appointmentType: order.appointmentType,
          serviceName: order.serviceName,
          serviceDescription: order.serviceDescription,
          description: order.description,
          price: order.price,
          numberPlate: order.numberPlate || null,
          bookedOn: (() => {
            if (!order.createdAt) return null;
            try {
              const d = order.createdAt.toDate?.() || new Date(order.createdAt);
              if (Number.isNaN(d.getTime())) return null;
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            } catch {
              return null;
            }
          })()
        };
      // Plain QR payload (no password, no verification URL)
      const qrData = JSON.stringify({ type: 'order', orderData });

      const qrCodeDataURL = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: { dark: '#1f2937', light: '#ffffff' }
      });

      return qrCodeDataURL;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  const handleOrderClick = async (order) => {
    setSelectedOrder(order);
    setQrModalOpen(true);
    
    // Generate QR code
    const qrCode = await generateQRCode(order);
    setQrCodeDataURL(qrCode);
  };

  const handleCloseQRModal = () => {
    setQrModalOpen(false);
    setSelectedOrder(null);
    setQrCodeDataURL('');
  };

  const handlePrintQRCode = () => {
    if (qrCodeDataURL && selectedOrder) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - ${selectedOrder.id}</title>
            <style>
              body { 
                margin: 0;
                padding: 0;
                background: #fff;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              .qr-code { 
                max-width: 100%;
                height: auto;
              }
              @media print {
                body { 
                  margin: 0; 
                  padding: 0;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                }
                .qr-code { 
                  max-width: 100%;
                  height: auto;
                }
              }
            </style>
          </head>
          <body>
            <img src="${qrCodeDataURL}" alt="QR Code" class="qr-code" />
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Filter and search functions
  const applyFiltersAndSearch = useCallback(() => {
    let filtered = [...orders];

         // Apply search
     if (searchTerm) {
       const searchLower = searchTerm.toLowerCase();
       
       // Check if search term contains multiple order IDs (comma or space separated)
       const searchTerms = searchTerm.split(/[,\s]+/).filter(term => term.trim() !== '');
       
       filtered = filtered.filter(order => {
         // If multiple search terms, check if order matches any of them
         if (searchTerms.length > 1) {
           return searchTerms.some(term => {
             const termLower = term.toLowerCase().trim();
             return order.id?.toLowerCase().includes(termLower) ||
                    order.customerPhone?.toLowerCase().includes(termLower) ||
                    order.userId?.toLowerCase().includes(termLower);
           });
         } else {
           // Single search term (original behavior)
           return order.id?.toLowerCase().includes(searchLower) ||
                  order.customerPhone?.toLowerCase().includes(searchLower) ||
                  order.userId?.toLowerCase().includes(searchLower);
         }
       });
     }

    // Apply filters
    if (filters.location) {
      filtered = filtered.filter(order => 
        order.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

         if (filters.date) {
      // filters.date is YYYY-MM-DD from the date input
      const filterDateKey = filters.date.includes('/')
        ? (() => {
            const [day, month, year] = filters.date.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          })()
        : filters.date;
      
      console.log('Filtering by appointment date:', filterDateKey);
      
      filtered = filtered.filter(order => {
        try {
          if (!order.appointmentDate) return false;

          // Normalize appointment date to YYYY-MM-DD (calendar day, same as display)
          let orderDateKey;
          if (typeof order.appointmentDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(order.appointmentDate)) {
            // Already a date-only / ISO string — use the calendar date part
            orderDateKey = order.appointmentDate.slice(0, 10);
          } else {
            let orderDate;
            if (order.appointmentDate?.toDate) {
              orderDate = order.appointmentDate.toDate();
            } else if (order.appointmentDate instanceof Date) {
              orderDate = order.appointmentDate;
            } else {
              orderDate = new Date(order.appointmentDate);
            }
            // Use UTC date parts to match formatDate() display
            const year = orderDate.getUTCFullYear();
            const month = String(orderDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(orderDate.getUTCDate()).padStart(2, '0');
            orderDateKey = `${year}-${month}-${day}`;
          }
          
          console.log('Comparing order date:', orderDateKey, 'with filter date:', filterDateKey);
          return orderDateKey === filterDateKey;
        } catch (error) {
          console.error('Error filtering by date:', error);
          return false;
        }
      });
    }

    if (filters.washType) {
      filtered = filtered.filter(order => 
        order.serviceType?.toLowerCase().includes(filters.washType.toLowerCase())
      );
    }

    if (filters.status) {
      filtered = filtered.filter(order => 
        order.status?.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.appointmentType) {
      filtered = filtered.filter(order => 
        order.appointmentType?.toLowerCase().includes(filters.appointmentType.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  }, [orders, filters, searchTerm]);

  // Update filtered orders when orders, filters, or search changes
  useEffect(() => {
    applyFiltersAndSearch();
  }, [applyFiltersAndSearch]);

  // Fetch orders data when switching to orders tab
  useEffect(() => {
    if (activeTab === 'orders') {
      console.log('Orders tab active, fetching orders data');
      fetchOrdersData();
    }
  }, [activeTab]);

  // Force fetch orders data on component mount if orders tab is active
  useEffect(() => {
    if (activeTab === 'orders') {
      console.log('Component mounted with orders tab active, fetching orders data');
      fetchOrdersData();
    }
  }, []);

  const fetchOrdersData = async () => {
    console.log('fetchOrdersData function called');
    try {
      // Fetch orders
      const ordersQuery = query(collection(db, 'orders'), orderBy('appointmentDate', 'desc'));
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Fetched orders:', ordersData);
      console.log('First order details:', ordersData[0]);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Filter users based on search term
  useEffect(() => {
    if (!userSearchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const searchLower = userSearchTerm.toLowerCase();
      const filtered = users.filter(user => 
        user.id?.toLowerCase().includes(searchLower) ||
        user.membershipNumber?.toLowerCase().includes(searchLower) ||
        user.area?.toLowerCase().includes(searchLower) ||
        user.contact?.toLowerCase().includes(searchLower)
      );
      setFilteredUsers(filtered);
    }
  }, [users, userSearchTerm]);

  const clearFilters = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const isoDate = `${year}-${month}-${day}`;
    setFilters({
      location: '',
      date: isoDate,
      dateDisplay: `${day}/${month}/${year}`,
      washType: '',
      status: '',
      appointmentType: ''
    });
    setSearchTerm('');
  };

  const clearUserSearch = () => {
    setUserSearchTerm('');
  };

  const handleStatusChange = async (orderId, newStatus) => {
    console.log('handleStatusChange called with:', orderId, newStatus);
    try {
      // Find the current order to preserve all its data
      const currentOrder = orders.find(order => order.id === orderId);
      if (!currentOrder) {
        alert('Order not found. Please refresh the page and try again.');
        return;
      }

      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state - preserve all original order data
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...currentOrder, status: newStatus, updatedAt: new Date() }
            : order
        )
      );
      
      alert(`Order ${orderId} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await deleteDoc(orderRef);
      // Update local state
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      alert(`Order ${orderId} has been deleted successfully.`);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. Please try again.');
    }
  };

  // Bulk selection functions
  const handleSelectOrder = (orderId) => {
    console.log('handleSelectOrder called with:', orderId);
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      if (newSelected.size < 150) {
        newSelected.add(orderId);
      } else {
        alert('Maximum 150 orders can be selected at once.');
      }
    }
    setSelectedOrders(newSelected);
  };

  const handleSelectAll = () => {
    console.log('handleSelectAll called');
    if (selectedOrders.size === filteredOrders.length) {
      // Deselect all
      setSelectedOrders(new Set());
    } else {
      // Select all (up to 150)
      const maxSelect = Math.min(filteredOrders.length, 150);
      const newSelected = new Set();
      for (let i = 0; i < maxSelect; i++) {
        newSelected.add(filteredOrders[i].id);
      }
      setSelectedOrders(newSelected);
      if (filteredOrders.length > 150) {
        alert(`Selected first 150 orders (maximum limit).`);
      }
    }
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus) {
      alert('Please select a status to apply.');
      return;
    }
    
    if (selectedOrders.size === 0) {
      alert('Please select at least one order.');
      return;
    }
    try {
      const promises = Array.from(selectedOrders).map(orderId => {
        const orderRef = doc(db, 'orders', orderId);
        return updateDoc(orderRef, {
          status: bulkStatus,
          updatedAt: new Date()
        });
      });

      await Promise.all(promises);
      
      // Update local state - preserve all original order data
      setOrders(prevOrders => 
        prevOrders.map(order => 
          selectedOrders.has(order.id) 
            ? { ...order, status: bulkStatus, updatedAt: new Date() }
            : order
        )
      );
      
      // Clear selection
      setSelectedOrders(new Set());
      setBulkStatus('');
      
      alert(`Successfully updated ${selectedOrders.size} orders to "${bulkStatus}".`);
    } catch (error) {
      console.error('Error updating bulk orders:', error);
      alert('Failed to update some orders. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) {
      alert('Please select at least one order.');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete ${selectedOrders.size} order(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const promises = Array.from(selectedOrders).map(orderId => {
        const orderRef = doc(db, 'orders', orderId);
        return deleteDoc(orderRef);
      });
      await Promise.all(promises);
      // Update local state
      setOrders(prevOrders => prevOrders.filter(order => !selectedOrders.has(order.id)));
      // Clear selection
      setSelectedOrders(new Set());
      alert(`Successfully deleted ${selectedOrders.size} orders.`);
    } catch (error) {
      console.error('Error deleting bulk orders:', error);
      alert('Failed to delete some orders. Please try again.');
    }
  };

  // Analytics functions
  const generateAnalyticsData = async () => {
    setLoadingAnalytics(true);
    try {
      const startDate = new Date(analyticsDateRange.startDate);
      const endDate = new Date(analyticsDateRange.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day

      // Filter orders within date range
      const filteredOrders = orders.filter(order => {
        try {
          let orderDate;
          if (order.appointmentDate?.toDate) {
            orderDate = order.appointmentDate.toDate();
          } else if (order.appointmentDate instanceof Date) {
            orderDate = order.appointmentDate;
          } else if (order.appointmentDate) {
            orderDate = new Date(order.appointmentDate);
          } else {
            return false;
          }
          return orderDate >= startDate && orderDate <= endDate;
        } catch (error) {
          return false;
        }
      });

      let data = {};

      switch (selectedAnalyticsTopic) {
        case 'profits':
          // Calculate daily profits
          const dailyProfits = {};
          filteredOrders.forEach(order => {
            const date = formatDate(order.appointmentDate);
            dailyProfits[date] = (dailyProfits[date] || 0) + (order.price || 0);
          });
          data = {
            type: 'line',
            title: 'Daily Profits',
            labels: Object.keys(dailyProfits),
            values: Object.values(dailyProfits),
            total: filteredOrders.reduce((sum, order) => sum + (order.price || 0), 0)
          };
          break;

        case 'bestWash':
          // Rank washes by popularity
          const washCounts = {};
          filteredOrders.forEach(order => {
            const washName = order.serviceName || order.serviceType || 'Unknown Service';
            washCounts[washName] = (washCounts[washName] || 0) + 1;
          });
          const sortedWashes = Object.entries(washCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 10); // Top 10
          data = {
            type: 'bar',
            title: 'Most Popular Wash Services',
            labels: sortedWashes.map(([name]) => name),
            values: sortedWashes.map(([,count]) => count)
          };
          break;

        case 'leastUsedTimeslots':
          // Find least used timeslots
          const timeslotCounts = {};
          filteredOrders.forEach(order => {
            const time = order.appointmentTime;
            if (time) {
              timeslotCounts[time] = (timeslotCounts[time] || 0) + 1;
            }
          });
          const sortedTimeslots = Object.entries(timeslotCounts)
            .sort(([,a], [,b]) => a - b)
            .slice(0, 10); // Bottom 10
          data = {
            type: 'bar',
            title: 'Least Used Timeslots',
            labels: sortedTimeslots.map(([time]) => formatTime12Hour(time)),
            values: sortedTimeslots.map(([,count]) => count)
          };
          break;

        case 'location':
          // Most used locations
          const locationCounts = {};
          filteredOrders.forEach(order => {
            const location = order.location || 'Unknown Location';
            locationCounts[location] = (locationCounts[location] || 0) + 1;
          });
          const sortedLocations = Object.entries(locationCounts)
            .sort(([,a], [,b]) => b - a);
          data = {
            type: 'pie',
            title: 'Most Used Locations',
            labels: sortedLocations.map(([location]) => location),
            values: sortedLocations.map(([,count]) => count)
          };
          break;

        case 'area':
          // Highest area usage
          const areaCounts = {};
          filteredOrders.forEach(order => {
            const area = order.area || 'Unknown Area';
            areaCounts[area] = (areaCounts[area] || 0) + 1;
          });
          const sortedAreas = Object.entries(areaCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 10); // Top 10
          data = {
            type: 'bar',
            title: 'Highest Area Usage',
            labels: sortedAreas.map(([area]) => area),
            values: sortedAreas.map(([,count]) => count)
          };
          break;

        default:
          data = null;
      }

      setAnalyticsData(data);
    } catch (error) {
      console.error('Error generating analytics:', error);
      alert('Failed to generate analytics. Please try again.');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Generate analytics when topic or date range changes
  useEffect(() => {
    if (orders.length > 0) {
      generateAnalyticsData();
    }
  }, [selectedAnalyticsTopic, analyticsDateRange, orders]);

  // Simple chart rendering functions
  const renderChart = (data) => {
    if (!data) return null;

    const maxValue = Math.max(...data.values);
    const chartHeight = 300;

    switch (data.type) {
      case 'line':
        return (
          <div style={{ height: chartHeight, position: 'relative', marginTop: '1rem' }}>
            <svg width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
              {data.values.map((value, index) => {
                const x = (index / (data.values.length - 1)) * 100;
                const y = chartHeight - (value / maxValue) * (chartHeight - 40);
                const nextValue = data.values[index + 1];
                const nextX = ((index + 1) / (data.values.length - 1)) * 100;
                const nextY = nextValue ? chartHeight - (nextValue / maxValue) * (chartHeight - 40) : y;
                
                return (
                  <g key={index}>
                    <circle
                      cx={`${x}%`}
                      cy={y}
                      r="4"
                      fill="#3b82f6"
                    />
                    {index < data.values.length - 1 && (
                      <line
                        x1={`${x}%`}
                        y1={y}
                        x2={`${nextX}%`}
                        y2={nextY}
                        stroke="#3b82f6"
                        strokeWidth="2"
                      />
                    )}
                    <text
                      x={`${x}%`}
                      y={chartHeight + 20}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#6b7280"
                    >
                      {data.labels[index]}
                    </text>
                    <text
                      x={`${x}%`}
                      y={y - 10}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#1f2937"
                      fontWeight="bold"
                    >
                      ${value.toFixed(2)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        );

      case 'bar':
        return (
          <div style={{ height: chartHeight, position: 'relative', marginTop: '1rem' }}>
            <svg width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
              {data.values.map((value, index) => {
                const barWidth = 80 / data.values.length;
                const x = (index / data.values.length) * 100 + (barWidth / 2);
                const barHeight = (value / maxValue) * (chartHeight - 60);
                const y = chartHeight - barHeight - 20;
                
                return (
                  <g key={index}>
                    <rect
                      x={`${x - barWidth/2}%`}
                      y={y}
                      width={`${barWidth}%`}
                      height={barHeight}
                      fill="#10b981"
                      rx="4"
                    />
                    <text
                      x={`${x}%`}
                      y={chartHeight + 20}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#6b7280"
                      transform={`rotate(-45 ${x}% ${chartHeight + 20})`}
                    >
                      {data.labels[index]}
                    </text>
                    <text
                      x={`${x}%`}
                      y={y - 5}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#1f2937"
                      fontWeight="bold"
                    >
                      {value}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        );

      case 'pie':
        const total = data.values.reduce((sum, val) => sum + val, 0);
        let currentAngle = 0;
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];
        
        return (
          <div style={{ height: chartHeight, position: 'relative', marginTop: '1rem' }}>
            <svg width="100%" height={chartHeight} style={{ overflow: 'visible' }}>
              <g transform={`translate(50%, 50%)`}>
                {data.values.map((value, index) => {
                  const percentage = value / total;
                  const angle = percentage * 360;
                  const radius = 80;
                  const startAngle = currentAngle;
                  const endAngle = currentAngle + angle;
                  
                  const x1 = radius * Math.cos((startAngle - 90) * Math.PI / 180);
                  const y1 = radius * Math.sin((startAngle - 90) * Math.PI / 180);
                  const x2 = radius * Math.cos((endAngle - 90) * Math.PI / 180);
                  const y2 = radius * Math.sin((endAngle - 90) * Math.PI / 180);
                  
                  const largeArcFlag = angle > 180 ? 1 : 0;
                  
                  const pathData = [
                    `M 0 0`,
                    `L ${x1} ${y1}`,
                    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                    'Z'
                  ].join(' ');
                  
                  currentAngle += angle;
                  
                  return (
                    <g key={index}>
                      <path
                        d={pathData}
                        fill={colors[index % colors.length]}
                        stroke="#fff"
                        strokeWidth="2"
                      />
                      <text
                        x={x1 * 0.6}
                        y={y1 * 0.6}
                        textAnchor="middle"
                        fontSize="12"
                        fill="#fff"
                        fontWeight="bold"
                      >
                        {Math.round(percentage * 100)}%
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              {data.labels.map((label, index) => (
                <div key={index} style={{ display: 'inline-block', margin: '0 1rem', fontSize: '0.9rem' }}>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: colors[index % colors.length],
                    marginRight: '0.5rem',
                    borderRadius: '2px'
                  }}></span>
                  {label} ({data.values[index]})
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f9fafb'
      }}>
        <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>Loading Admin Panel...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
           <h1 style={{ color: '#f97316', margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
             🚗 Rocket Express Auto Wash TT - Admin Panel
           </h1>
           {isViewOnlyAdmin && (
             <div style={{
               background: '#f59e0b',
               color: '#fff',
               padding: '0.25rem 0.75rem',
               borderRadius: '20px',
               fontSize: '0.8rem',
               fontWeight: '600'
             }}>
               👁️ View Only Mode
             </div>
           )}
         </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {adminSession && (
            <div style={{
              textAlign: 'right',
              fontSize: '0.85rem',
              color: '#374151',
              lineHeight: 1.4
            }}>
              <div style={{ fontWeight: 600 }}>Signed in as</div>
              <div style={{ color: '#1f2937' }}>{adminSession.email}</div>
              <div style={{
                color: adminSession.role === 'Full admin' ? '#059669' : '#d97706',
                fontWeight: 600
              }}>
                {adminSession.role}
              </div>
            </div>
          )}
        <button
          onClick={handleLogout}
          style={{
            background: '#ef4444',
            color: '#fff',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Logout
        </button>
        </div>
      </div>

      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        {/* Sidebar Navigation */}
        <div style={{
          width: '250px',
          background: '#fff',
          borderRight: '1px solid #e5e7eb',
          padding: '1rem 0'
        }}>
                     {[
             ...(isViewOnlyAdmin ? [] : [{ id: 'dashboard', label: '📊 Dashboard', icon: '📊' }]),
             { id: 'orders', label: '📋 Orders', icon: '📋' },
             { id: 'users', label: '👥 Users', icon: '👥' },
             ...(isViewOnlyAdmin ? [] : [{ id: 'analytics', label: '📈 Analytics', icon: '📈' }]),
             ...(isViewOnlyAdmin ? [] : [{ id: 'washCodes', label: '🔑 Wash Codes', icon: '🔑' }]),
             ...(isViewOnlyAdmin ? [] : [{ id: 'settings', label: '⚙️ Settings', icon: '⚙️' }])
           ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                console.log('Tab clicked:', tab.id);
                setActiveTab(tab.id);
              }}
              style={{
                width: '100%',
                padding: '0.75rem 1.5rem',
                background: activeTab === tab.id ? '#f97316' : 'transparent',
                color: activeTab === tab.id ? '#fff' : '#374151',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: activeTab === tab.id ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: '2rem' }}>
                     {activeTab === 'dashboard' && (
             <div>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                 <h2 style={{ color: '#1f2937', fontSize: '1.8rem', margin: 0 }}>
                   Dashboard Overview
                 </h2>
                 <button
                   onClick={fetchData}
                   style={{
                     background: '#10b981',
                     color: '#fff',
                     border: 'none',
                     padding: '0.5rem 1rem',
                     borderRadius: '6px',
                     cursor: 'pointer',
                     fontSize: '0.9rem',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '0.5rem'
                   }}
                 >
                   🔄 Refresh Data
                 </button>
               </div>
              
              {/* Stats Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{
                  background: '#fff',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Monthly Revenue</div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>${stats.totalRevenue.toFixed(2)}</div>
                </div>
                
                                 <div style={{
                   background: '#fff',
                   padding: '1.5rem',
                   borderRadius: '12px',
                   boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                   border: '1px solid #e5e7eb'
                 }}>
                   <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Today's Orders</div>
                   <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>{stats.todayOrders}</div>
                 </div>
                 
                                   <div style={{
                    background: '#fff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Today's Top Wash</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981', wordBreak: 'break-word' }}>{stats.todayTopWash}</div>
                  </div>
                 
                 <div style={{
                   background: '#fff',
                   padding: '1.5rem',
                   borderRadius: '12px',
                   boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                   border: '1px solid #e5e7eb'
                 }}>
                   <div style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Today's Revenue</div>
                   <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>${stats.todayRevenue.toFixed(2)}</div>
                 </div>
              </div>

              {/* Recent Orders */}
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                  <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.2rem' }}>Recent Orders</h3>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {orders.slice(0, 10).map(order => (
                    <div key={order.id} style={{
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #f3f4f6',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1f2937' }}>
                          {order.customerName || 'Unknown Customer'}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                          {formatDate(order.appointmentDate)} at {order.appointmentTime}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '20px', 
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          background: getStatusColor(order.status) + '20',
                          color: getStatusColor(order.status)
                        }}>
                          {order.status}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#10b981', fontWeight: '600' }}>
                          ${order.price || 0}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

                                           {activeTab === 'orders' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2 style={{ color: '#1f2937', fontSize: '1.8rem', margin: 0 }}>
                    All Orders ({filteredOrders.length} of {orders.length})
                  </h2>
                  <button
                    onClick={fetchOrdersData}
                    style={{
                      background: '#10b981',
                      color: '#fff',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🔄 Refresh Orders
                  </button>
                </div>
               
                               {/* Search Bar */}
                <div style={{ marginBottom: '1.5rem' }}>
                                    <input
                     type="text"
                     placeholder="Search by Order ID, Phone, or Member ID... (Multiple IDs: separate with comma or space)"
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontSize: '1rem',
                      marginBottom: '1rem'
                    }}
                  />
                </div>

                                 {/* Bulk Actions */}
                 {selectedOrders.size > 0 && !isViewOnlyAdmin && (
                  <div style={{ 
                    background: '#f0f9ff', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    border: '1px solid #0ea5e9',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '1rem', 
                      flexWrap: 'wrap' 
                    }}>
                      <div style={{ fontWeight: '600', color: '#0c4a6e' }}>
                        {selectedOrders.size} order(s) selected
                      </div>
                      
                      <select
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #0ea5e9',
                          fontSize: '0.9rem',
                          background: '#fff'
                        }}
                      >
                        <option value="">Select Status</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="trading">Trading</option>
                      </select>
                      
                      <button
                        onClick={handleBulkStatusChange}
                        disabled={!bulkStatus}
                        style={{
                          padding: '0.5rem 1rem',
                          background: bulkStatus ? '#0ea5e9' : '#9ca3af',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: bulkStatus ? 'pointer' : 'not-allowed',
                          fontSize: '0.9rem'
                        }}
                      >
                        Update Status
                      </button>
                      
                      <button
                        onClick={handleBulkDelete}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        Delete Selected
                      </button>
                      
                      <button
                        onClick={() => setSelectedOrders(new Set())}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#6b7280',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>
                )}

               {/* Filter Options */}
               <div style={{ 
                 background: '#fff', 
                 padding: '1.5rem', 
                 borderRadius: '12px', 
                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                 border: '1px solid #e5e7eb',
                 marginBottom: '1.5rem'
               }}>
                 <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.1rem' }}>Filters</h3>
                 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                   <div>
                     <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                       Wash Location
                     </label>
                     <input
                       type="text"
                       placeholder="Enter location..."
                       value={filters.location}
                       onChange={(e) => setFilters({...filters, location: e.target.value})}
                       style={{
                         width: '100%',
                         padding: '0.5rem',
                         borderRadius: '6px',
                         border: '1px solid #e5e7eb',
                         fontSize: '0.9rem'
                       }}
                     />
                   </div>
                   
                   <div>
                     <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                       Date
                     </label>
                     <div style={{ position: 'relative' }}>
                       <input
                         type="date"
                         value={filters.date || ''}
                         onChange={(e) => {
                           const isoDate = e.target.value; // YYYY-MM-DD format
                           if (isoDate) {
                             // Convert YYYY-MM-DD to DD/MM/YYYY for display
                             const [year, month, day] = isoDate.split('-');
                             const displayDate = `${day}/${month}/${year}`;
                             setFilters({...filters, date: isoDate, dateDisplay: displayDate});
                           } else {
                             setFilters({...filters, date: '', dateDisplay: ''});
                           }
                         }}
                         style={{
                           width: '100%',
                           padding: '0.5rem',
                           borderRadius: '6px',
                           border: '1px solid #e5e7eb',
                           fontSize: '0.9rem'
                         }}
                       />
                       {/* Custom display overlay to show DD/MM/YYYY format */}
                       {filters.dateDisplay && (
                         <div
                           style={{
                             position: 'absolute',
                             top: '50%',
                             left: '0.5rem',
                             transform: 'translateY(-50%)',
                             pointerEvents: 'none',
                             color: '#374151',
                             fontSize: '0.9rem',
                             backgroundColor: 'white',
                             padding: '0 0.25rem'
                           }}
                         >
                           {filters.dateDisplay}
                         </div>
                       )}
                     </div>
                   </div>
                   
                                       <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                        Wash Type
                      </label>
                      <select
                        value={filters.washType}
                        onChange={(e) => setFilters({...filters, washType: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          fontSize: '0.9rem'
                        }}
                      >
                        <option value="">All Wash Types</option>
                                                 {getWashPackages().map(pkg => (
                           <option key={pkg.id} value={pkg.name}>
                             {pkg.name}
                           </option>
                         ))}
                      </select>
                    </div>
                   
                   <div>
                     <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                       Status
                     </label>
                     <select
                       value={filters.status}
                       onChange={(e) => setFilters({...filters, status: e.target.value})}
                       style={{
                         width: '100%',
                         padding: '0.5rem',
                         borderRadius: '6px',
                         border: '1px solid #e5e7eb',
                         fontSize: '0.9rem'
                       }}
                     >
                       <option value="">All Statuses</option>
                       <option value="pending">Pending</option>
                       <option value="completed">Completed</option>
                       <option value="cancelled">Cancelled</option>
                       <option value="trading">Trading</option>
                     </select>
                   </div>
                   
                                       <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                        Appointment Type
                      </label>
                      <select
                        value={filters.appointmentType}
                        onChange={(e) => setFilters({...filters, appointmentType: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          fontSize: '0.9rem'
                        }}
                      >
                        <option value="">All Appointment Types</option>
                        <option value="Free">Free</option>
                        <option value="Special">Special</option>
                      </select>
                    </div>
                 </div>
                 
                 <button
                   onClick={clearFilters}
                   style={{
                     marginTop: '1rem',
                     padding: '0.5rem 1rem',
                     background: '#6b7280',
                     color: '#fff',
                     border: 'none',
                     borderRadius: '6px',
                     cursor: 'pointer',
                     fontSize: '0.9rem'
                   }}
                 >
                   Clear All Filters
                 </button>
               </div>
               
                               <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden'
                }}>
                  {/* Select All Header */}
                  {!isViewOnlyAdmin && (
                    <div style={{
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      background: '#f9fafb'
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedOrders.size === filteredOrders.length && filteredOrders.length > 0}
                        onChange={(e) => {
                          console.log('Select All checkbox onChange triggered');
                          handleSelectAll();
                        }}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{ 
                        fontWeight: '600', 
                        color: '#374151',
                        fontSize: '0.9rem'
                      }}>
                        Select All ({selectedOrders.size} of {filteredOrders.length})
                      </span>
                    </div>
                  )}
                  
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {filteredOrders.map(order => (
                                                               <div key={order.id} style={{
                        padding: '1.5rem',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'grid',
                        gridTemplateColumns: isViewOnlyAdmin ? '1fr 1fr 1fr 1fr 1fr 1fr' : 'auto 1fr 1fr 1fr 1fr 1fr 1fr',
                        gap: '1rem',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        borderRadius: '8px',
                        margin: '0.25rem 0'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      onClick={() => handleOrderClick(order)}>
                        {!isViewOnlyAdmin && (
                          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedOrders.has(order.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectOrder(order.id);
                              }}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>
                            Order ID
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                            {getDisplayOrderId(order) || 'N/A'}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>
                            Order Code
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                            {order.orderCode || 'N/A'}
                          </div>
                        </div>
                       
                       <div>
                         <div style={{ fontWeight: '600', color: '#1f2937' }}>
                           User ID
                         </div>
                         <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                           {order.userId || 'N/A'}
                         </div>
                       </div>
                       
                       <div>
                         <div style={{ fontWeight: '600', color: '#1f2937' }}>
                           Date & Time
                         </div>
                         <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                           {formatDate(order.appointmentDate)} at {order.appointmentTime}
                         </div>
                       </div>
                       
                       <div>
                         <div style={{ fontWeight: '600', color: '#1f2937' }}>
                           Location
                         </div>
                         <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                           {order.location || 'No location'}
                         </div>
                       </div>
                       
                       <div>
                          <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                            Status
                          </div>
                          <div 
                            style={{ 
                              padding: '0.25rem 0.75rem', 
                              borderRadius: '20px', 
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background: getStatusColor(order.status) + '20',
                              color: getStatusColor(order.status),
                              display: 'inline-block',
                              marginBottom: '0.5rem',
                              cursor: 'default'
                            }}
                          >
                            {order.status}
                          </div>
                          {!isViewOnlyAdmin && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              <select
                                value={order.status}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(order.id, e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid #e5e7eb',
                                  fontSize: '0.8rem',
                                  width: '100%'
                                }}
                              >
                                <option value="confirmed">Confirmed</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="trading">Trading</option>
                              </select>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
                                    handleDeleteOrder(order.id);
                                  }
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  background: '#ef4444',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  width: '100%'
                                }}
                                title="Delete this order"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          )}
                        </div>
                     </div>
                  ))}
                </div>
              </div>
            </div>
          )}

                                           {activeTab === 'users' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2 style={{ color: '#1f2937', fontSize: '1.8rem', margin: 0 }}>
                    All Users ({filteredUsers.length} of {users.length})
                  </h2>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={fetchUserBlocks}
                      style={{
                        background: '#f59e0b',
                        color: '#fff',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔄 Refresh Blocks
                    </button>
                    <button
                      onClick={fetchData}
                      style={{
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔄 Refresh Data
                    </button>
                  </div>
                </div>
               
               {/* Search Bar */}
               <div style={{ marginBottom: '1.5rem' }}>
                                   <input
                    type="text"
                    placeholder="Search by User ID, Member ID, Location, or Contact Number..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                   style={{
                     width: '100%',
                     padding: '0.75rem',
                     borderRadius: '8px',
                     border: '1px solid #e5e7eb',
                     fontSize: '1rem',
                     marginBottom: '1rem'
                   }}
                 />
                 {userSearchTerm && (
                   <button
                     onClick={clearUserSearch}
                     style={{
                       padding: '0.5rem 1rem',
                       background: '#6b7280',
                       color: '#fff',
                       border: 'none',
                       borderRadius: '6px',
                       cursor: 'pointer',
                       fontSize: '0.9rem'
                     }}
                   >
                     Clear Search
                   </button>
                 )}
               </div>
              
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden'
              }}>
                                 <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                   {filteredUsers.length === 0 ? (
                     <div style={{ 
                       padding: '2rem', 
                       textAlign: 'center', 
                       color: '#6b7280',
                       fontSize: '1.1rem'
                     }}>
                       {userSearchTerm ? 'No users found matching your search.' : 'No users found in the database.'}
                       <br />
                       <span style={{ fontSize: '0.9rem', marginTop: '0.5rem', display: 'block' }}>
                         {userSearchTerm ? 'Try adjusting your search terms.' : 'Users will appear here once they register accounts.'}
                       </span>
                     </div>
                   ) : (
                     filteredUsers.map(user => {
                        const userBlock = userBlocks[user.id];
                        const isBlocked = userBlock && userBlock.blockUntil > new Date();
                        
                        return (
                          <div key={user.id} style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid #f3f4f6',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                            gap: '1rem',
                            alignItems: 'center',
                            backgroundColor: isBlocked ? '#fef2f2' : 'transparent'
                          }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                User ID
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                {user.id || 'N/A'}
                              </div>
                            </div>
                            
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                Member ID
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                {user.membershipNumber || 'N/A'}
                              </div>
                            </div>
                            
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                Location
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                {user.area || 'No location'}
                              </div>
                            </div>
                            
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                                Contact Number
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                                {user.contact || 'No contact'}
                              </div>
                            </div>
                            
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                                Block Status
                              </div>
                              {isBlocked ? (
                                <div>
                                  <div style={{ 
                                    fontSize: '0.8rem', 
                                    color: '#dc2626',
                                    backgroundColor: '#fee2e2',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600'
                                  }}>
                                    🚫 BLOCKED ({userBlock.type})
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                    Until: {userBlock.blockUntil.toLocaleDateString()}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                    Reason: {userBlock.reason}
                                  </div>
                                  {!isViewOnlyAdmin && (
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to unblock this user from ${userBlock.type} appointments?`)) {
                                          unblockUser(user.id, userBlock.type);
                                        }
                                      }}
                                      style={{
                                        background: '#dc2626',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontWeight: '600'
                                      }}
                                    >
                                      Unblock
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  color: '#059669',
                                  backgroundColor: '#d1fae5',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}>
                                  ✅ Not Blocked
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                   )}
                </div>
              </div>
            </div>
          )}

                     {activeTab === 'analytics' && (
             <div>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                 <h2 style={{ color: '#1f2937', fontSize: '1.8rem', margin: 0 }}>
                   Analytics & Reports
                 </h2>
                 <button
                   onClick={fetchData}
                   style={{
                     background: '#10b981',
                     color: '#fff',
                     border: 'none',
                     padding: '0.5rem 1rem',
                     borderRadius: '6px',
                     cursor: 'pointer',
                     fontSize: '0.9rem',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '0.5rem'
                   }}
                 >
                   🔄 Refresh Data
                 </button>
               </div>

               {/* Analytics Controls */}
               <div style={{
                 background: '#fff',
                 padding: '1.5rem',
                 borderRadius: '12px',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                 border: '1px solid #e5e7eb',
                 marginBottom: '2rem'
               }}>
                 <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.1rem' }}>Analytics Settings</h3>
                 
                 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                   <div>
                     <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                       Start Date
                     </label>
                     <input
                       type="date"
                       value={analyticsDateRange.startDate}
                       onChange={(e) => setAnalyticsDateRange({...analyticsDateRange, startDate: e.target.value})}
                       style={{
                         width: '100%',
                         padding: '0.5rem',
                         borderRadius: '6px',
                         border: '1px solid #e5e7eb',
                         fontSize: '0.9rem'
                       }}
                     />
                   </div>
                   
                   <div>
                     <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                       End Date
                     </label>
                     <input
                       type="date"
                       value={analyticsDateRange.endDate}
                       onChange={(e) => setAnalyticsDateRange({...analyticsDateRange, endDate: e.target.value})}
                       style={{
                         width: '100%',
                         padding: '0.5rem',
                         borderRadius: '6px',
                         border: '1px solid #e5e7eb',
                         fontSize: '0.9rem'
                       }}
                     />
                   </div>
                   
                   <div>
                     <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                       Analytics Topic
                     </label>
                     <select
                       value={selectedAnalyticsTopic}
                       onChange={(e) => setSelectedAnalyticsTopic(e.target.value)}
                       style={{
                         width: '100%',
                         padding: '0.5rem',
                         borderRadius: '6px',
                         border: '1px solid #e5e7eb',
                         fontSize: '0.9rem'
                       }}
                     >
                       <option value="profits">💰 Profits - Total Revenue</option>
                       <option value="bestWash">🏆 Best Wash - Most Popular Services</option>
                       <option value="leastUsedTimeslots">⏰ Least Used Timeslots</option>
                       <option value="location">📍 Location - Most Used Locations</option>
                       <option value="area">🌍 Area - Highest Area Usage</option>
                     </select>
                   </div>
                 </div>
               </div>

               {/* Analytics Chart */}
               <div style={{
                 background: '#fff',
                 padding: '1.5rem',
                 borderRadius: '12px',
                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                 border: '1px solid #e5e7eb'
               }}>
                 {loadingAnalytics ? (
                   <div style={{ textAlign: 'center', padding: '2rem' }}>
                     <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>Generating analytics...</div>
                   </div>
                 ) : analyticsData ? (
                   <div>
                     <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937', fontSize: '1.3rem', textAlign: 'center' }}>
                       {analyticsData.title}
                     </h3>
                     
                     {analyticsData.total && (
                       <div style={{ 
                         textAlign: 'center', 
                         marginBottom: '1rem',
                         padding: '1rem',
                         background: '#f0f9ff',
                         borderRadius: '8px',
                         border: '1px solid #0ea5e9'
                       }}>
                         <div style={{ fontSize: '1.1rem', color: '#0c4a6e', fontWeight: '600' }}>
                           Total Revenue: ${analyticsData.total.toFixed(2)}
                         </div>
                       </div>
                     )}
                     
                     {analyticsData.values.length > 0 ? (
                       renderChart(analyticsData)
                     ) : (
                       <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                         <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>No data available</div>
                         <div>No orders found for the selected date range and topic.</div>
                       </div>
                     )}
                   </div>
                 ) : (
                   <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                     <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Select a topic to view analytics</div>
                     <div>Choose a date range and analytics topic to generate charts and insights.</div>
                   </div>
                 )}
               </div>
            </div>
          )}

          {activeTab === 'washCodes' && (
            <AdminWashCodes />
          )}

                     {activeTab === 'settings' && (
             <div>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                 <h2 style={{ color: '#1f2937', fontSize: '1.8rem', margin: 0 }}>
                   System Settings
                 </h2>
                 <button
                   onClick={fetchData}
                   style={{
                     background: '#10b981',
                     color: '#fff',
                     border: 'none',
                     padding: '0.5rem 1rem',
                     borderRadius: '6px',
                     cursor: 'pointer',
                     fontSize: '0.9rem',
                     display: 'flex',
                     alignItems: 'center',
                     gap: '0.5rem'
                   }}
                 >
                   🔄 Refresh Data
                 </button>
               </div>
              
              

              <div style={{ color: '#6b7280', textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚙️</div>
                <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Settings Coming Soon</div>
                <div>Business hours, notifications, and system configuration will be available here.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModalOpen && selectedOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{
                margin: 0,
                color: '#1f2937',
                fontSize: '1.5rem'
              }}>
                Order QR Code
              </h2>
              <button
                onClick={handleCloseQRModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.25rem'
                }}
                title="Close"
              >
                ×
              </button>
            </div>

            {qrCodeDataURL ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: '#f9fafb',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <img 
                    src={qrCodeDataURL} 
                    alt="QR Code" 
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div style={{
                  background: '#f3f4f6',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  textAlign: 'left'
                }}>
                  <h3 style={{
                    margin: '0 0 0.75rem 0',
                    color: '#1f2937',
                    fontSize: '1.1rem',
                    textAlign: 'center'
                  }}>
                    Order Details
                  </h3>
                  <div style={{ fontSize: '0.9rem', color: '#374151' }}>
                    <p style={{ margin: '0.25rem 0' }}><strong>Order ID:</strong> {selectedOrder.id}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Order Code:</strong> {selectedOrder.orderCode || 'N/A'}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Service:</strong> {selectedOrder.serviceName || 'N/A'}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Date:</strong> {formatDate(selectedOrder.appointmentDate)}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Time:</strong> {selectedOrder.appointmentTime}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Location:</strong> {selectedOrder.location || 'N/A'}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Price:</strong> ${selectedOrder.price || 0}</p>
                    <p style={{ margin: '0.25rem 0' }}><strong>Status:</strong> {selectedOrder.status}</p>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={handlePrintQRCode}
                    style={{
                      background: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🖨️ Print QR Code
                  </button>
                  <button
                    onClick={handleCloseQRModal}
                    style={{
                      background: '#6b7280',
                      color: '#fff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Generating QR Code...</div>
                <div>Please wait while the QR code is being generated.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
